import { useEffect, useState } from "react";
import { IndianRupee, X, Save, Loader2 } from "lucide-react";

export default function ExamFeeModal({
  open,
  onClose,
  currentFee,
  onSave,
}) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // ================================================
  // SET CURRENT FEE
  // ================================================

  useEffect(() => {
    if (open) {
      setAmount(
        currentFee !== null &&
        currentFee !== undefined
          ? String(currentFee)
          : ""
      );

      setError("");
    }
  }, [open, currentFee]);

  // ================================================
  // CLOSE
  // ================================================

  function handleClose() {
    if (saving) return;

    setError("");
    onClose();
  }

  // ================================================
  // SAVE
  // ================================================

  async function handleSubmit(e) {
    e.preventDefault();

    setError("");

    const numericAmount = Number(amount);

    if (
      amount === "" ||
      !Number.isFinite(numericAmount)
    ) {
      setError("Please enter a valid exam fee.");
      return;
    }

    if (numericAmount < 0) {
      setError("Exam fee cannot be negative.");
      return;
    }

    try {
      setSaving(true);

      await onSave(numericAmount);

      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update exam fee."
      );
    } finally {
      setSaving(false);
    }
  }

  // ================================================
  // DON'T RENDER
  // ================================================

  if (!open) {
    return null;
  }

  // ================================================
  // MODAL
  // ================================================

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      data-testid="exam-fee-modal"
    >
      {/* Overlay */}

      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}

      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}

        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-900 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F5A623] text-slate-900">
                <IndianRupee className="h-5 w-5" />
              </div>

              <div>
                <h2 className="font-heading text-lg font-bold text-white">
                  {currentFee !== null &&
                  currentFee !== undefined
                    ? "Update Exam Fee"
                    : "Add Exam Fee"}
                </h2>

                <p className="text-xs text-slate-400">
                  CET application examination fee
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close"
            data-testid="exam-fee-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}

        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <label
              htmlFor="exam-fee-amount"
              className="mb-2 block text-sm font-bold text-slate-700"
            >
              Exam Fee
            </label>

            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />

              <input
                id="exam-fee-amount"
                type="number"
                min="0"
                step="1"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setError("");
                }}
                placeholder="Enter exam fee"
                disabled={saving}
                autoFocus
                className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-10 pr-4 text-lg font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20 disabled:bg-slate-100"
                data-testid="exam-fee-input"
              />
            </div>

            <p className="mt-2 text-xs text-slate-500">
              Enter the examination fee in Indian Rupees.
            </p>

            {/* Error */}

            {error && (
              <div
                className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700"
                data-testid="exam-fee-error"
              >
                {error}
              </div>
            )}
          </div>

          {/* Footer */}

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="exam-fee-cancel-btn"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#F5A623] px-5 py-2.5 text-sm font-bold text-slate-900 transition hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="exam-fee-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {currentFee !== null &&
                  currentFee !== undefined
                    ? "Update Fee"
                    : "Save Fee"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}